import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { TimestampCell } from "_shared/_views/tables/date-cells";
import { NoValueDash } from "_shared/_views/tables/no-value-dash";
import { useListAssistantFeedbackQuery } from "ai-assistant/_api/ai-assistant-api-service";
import { AnswerFeedbackCategory, describeAnswerFeedbackReason } from "ai-assistant/feedback/answer-feedback-reason";
import AnswerFeedbackReport from "ai-assistant/feedback/answer-feedback-report";
import { AssistantGroundingState } from "ai-assistant/grounding/assistant-grounding-state";
import React from "react";

const Section = styled("section")(({ theme }) => ({
  marginTop: theme.spacing(3),
}));

// On one line: an identifier broken every few characters cannot be read, compared or copied whole.
// The table scrolls sideways instead.
const Code = styled("code")(({ theme }) => ({
  fontFamily: "monospace",
  fontSize: theme.typography.caption.fontSize,
  whiteSpace: "nowrap",
}));

const groundingLabel = (state: AssistantGroundingState): string => {
  switch (state) {
    case AssistantGroundingState.Grounded:
      return "Grounded";
    case AssistantGroundingState.Unverified:
      return "Unverified";
    case AssistantGroundingState.Unverifiable:
      return "Not fully checked";
    case AssistantGroundingState.NotApplicable:
      return "No sources";
  }
};

interface QueueProps {
  category: AnswerFeedbackCategory;
  title: string;
  description: string;
  empty: string;
}

function Queue(props: QueueProps): React.JSX.Element {
  const headingId = React.useId();
  const { data, isLoading, isError } = useListAssistantFeedbackQuery(props.category);

  return (
    <Section aria-labelledby={headingId}>
      <Typography id={headingId} variant="h6" component="h2">
        {props.title}
        {data !== undefined && ` (${data.length})`}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {props.description}
      </Typography>
      {isLoading && <CircularProgress size={24} aria-label={`Loading ${props.title.toLowerCase()}`} />}
      {isError && <Alert severity="error">These reports could not be loaded.</Alert>}
      {data !== undefined && data.length === 0 && (
        <Typography variant="body2">{props.empty}</Typography>
      )}
      {data !== undefined && data.length > 0 && (
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small" aria-labelledby={headingId}>
            <TableHead>
              <TableRow>
                <TableCell>Reported</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Comment</TableCell>
                <TableCell>Trace id</TableCell>
                <TableCell>Grounding</TableCell>
                <TableCell>Prompt / tools</TableCell>
                <TableCell>Model</TableCell>
                <TableCell>Legal entity</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((report: AnswerFeedbackReport) => (
                <TableRow key={report.id}>
                  <TableCell><TimestampCell value={report.reportedAt} /></TableCell>
                  <TableCell>{describeAnswerFeedbackReason(report.reason)}</TableCell>
                  <TableCell sx={{ minWidth: 200 }}>{report.comment ?? <NoValueDash />}</TableCell>
                  <TableCell>{report.traceId === undefined ? <NoValueDash /> : <Code>{report.traceId}</Code>}</TableCell>
                  <TableCell>{groundingLabel(report.groundingState)}</TableCell>
                  <TableCell>
                    {report.promptVersion === undefined ? <NoValueDash /> : <Code>{report.promptVersion}</Code>}
                    <br />
                    {report.toolSchemaVersion === undefined ? <NoValueDash /> : <Code>{report.toolSchemaVersion}</Code>}
                  </TableCell>
                  <TableCell>{report.model === undefined ? <NoValueDash /> : <Code>{report.model}</Code>}</TableCell>
                  <TableCell><Code>{report.legalEntityId}</Code></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Section>
  );
}

/**
 * Where reports that an answer was wrong are read (#5204).
 *
 * Two queues, access first. A report that the assistant showed someone what they should not see
 * is a security report and is also raised as an alert when it is made; it is listed apart so it is
 * never read as one more "incomplete". The trace id is how a report becomes something to
 * investigate - see "From a reported answer to a grounding case" in rag/evaluation/README.md.
 */
export default function AssistantFeedbackQueue(): React.JSX.Element {
  return (
    <Box>
      <Queue
        category={AnswerFeedbackCategory.Access}
        title="Access reports"
        description="Someone says an answer showed them something they should not have been able to see. Each one also raised an alert when it was made."
        empty="No access reports."
      />
      <Queue
        category={AnswerFeedbackCategory.Quality}
        title="Quality reports"
        description="Answers people said were wrong, incomplete, or cited the wrong source. Newest first."
        empty="No quality reports."
      />
    </Box>
  );
}
